import { HttpClient, HttpParams } from '@angular/common/http';
import { DOCUMENT, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, firstValueFrom, map, of } from 'rxjs';

import { AuthService } from '../../shared/auth/auth.service';
import { environment } from '../../../environments/environment';
import { UiIconComponent } from '../../shared/ui/icon/icon.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ThemeService } from '../../shared/theme/theme.service';
import {
  createLandingContent,
  FooterColumn,
  LandingMetric,
  LandingComparisonPoint,
  LandingUseCaseTeam,
  LandingWorkflowCard,
} from './landing.content';
import { PaymentPlan } from '../../shared/payments/payments.models';
import { PlanGridComponent } from '../../shared/payments/plan-grid/plan-grid.component';
import { PaymentsApiService } from '../../shared/payments/payments-api.service';
import { UtmTrackingService } from '../../shared/tracking/utm-tracking.service';
import { FaqPageComponent } from '../faq/faq-page.component';

type LandingState = 'idle' | 'exchanging';

interface ComparisonRow {
  id: string;
  generic: LandingComparisonPoint | null;
  askpc: LandingComparisonPoint | null;
}

interface CoverageMetricApiRow {
  label: string;
  count: number;
}

function buildComparisonRows(
  generic: LandingComparisonPoint[],
  askpc: LandingComparisonPoint[],
): ComparisonRow[] {
  const n = Math.max(generic.length, askpc.length);
  return Array.from({ length: n }, (_, i) => ({
    id: `cmp-${i}`,
    generic: generic[i] ?? null,
    askpc: askpc[i] ?? null,
  }));
}

interface LandingPlan {
  id: string;
  name: string;
  price: string;
  period?: string;
  subline?: string;
  highlight?: 'recommended' | 'save';
  ctaLabel: string;
  bullet?: string;
}

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: [
    './landing-page.component.scss',
    './styles/landing-core.scss',
    './styles/landing-content.scss',
    './styles/landing-usecases.scss',
    './styles/landing-pricing.scss',
    './styles/landing-responsive-lg.scss',
    './styles/landing-responsive-sm.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  imports: [NgOptimizedImage, UiIconComponent, PlanGridComponent, FaqPageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageComponent implements AfterViewInit, OnDestroy {
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly theme = inject(ThemeService);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly paymentsApi = inject(PaymentsApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly utmTracking = inject(UtmTrackingService);
  currentYear: number = new Date().getFullYear();
  private readonly content = createLandingContent(environment);
  private readonly _state = signal<LandingState>('idle');
  readonly state = this._state.asReadonly();
  readonly isExchanging = computed(() => this.state() === 'exchanging');

  // Full-screen startup loader:
  // - shown while we determine auth via `/session`
  // - plus 1s delay after auth is known (per requirement)
  readonly startupLoading = signal(true);
  readonly showStartupLoader = computed(() => this.startupLoading() || this.isExchanging());
  private startupLoaderTimeoutId: number | null = null;
  private didKickHeroVideo = false;

  private readonly breakpointState = toSignal<BreakpointState>(
    this.breakpoints.observe(['(max-width: 1250px)', '(max-width: 720px)']),
  );

  readonly isBelow1250 = computed(
    () => this.breakpointState()?.breakpoints?.['(max-width: 1250px)'] ?? false,
  );

  readonly isBelow720 = computed(
    () => this.breakpointState()?.breakpoints?.['(max-width: 720px)'] ?? false,
  );

  readonly marqueeLogos = this.content.marqueeLogos;

  readonly isMarqueePaused = signal(false);

  @ViewChild('marqueeRail') private marqueeRail?: ElementRef<HTMLElement>;
  private marqueeResumeListener: ((e: MouseEvent) => void) | null = null;

  onMarqueeTap() {
    const w = this.doc.defaultView;
    if (!w) return;

    // Only enable tap-to-toggle on touch/coarse pointers.
    const isTouch = w.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (!isTouch) return;

    // Touch behavior:
    // - tap marquee once -> pause
    // - then ANY next tap anywhere -> resume
    if (!this.isMarqueePaused()) {
      this.isMarqueePaused.set(true);
      this.armMarqueeResumeOnNextTap();
      return;
    }
  }

  private armMarqueeResumeOnNextTap() {
    const w = this.doc.defaultView;
    if (!w) return;

    // Avoid arming on the same click that just paused it.
    w.setTimeout(() => {
      this.disarmMarqueeResumeOnNextTap();

      const handler = (e: MouseEvent) => {
        this.isMarqueePaused.set(false);
        this.disarmMarqueeResumeOnNextTap();

        // If the "resume" tap is on the marquee itself, prevent the marquee click
        // from immediately pausing it again.
        const rail = this.marqueeRail?.nativeElement ?? null;
        const target = e.target as Node | null;
        if (rail && target && rail.contains(target)) {
          e.stopPropagation();
        }
      };

      this.marqueeResumeListener = handler;
      // Capture=true so we can stop propagation only for marquee taps.
      this.doc.addEventListener('click', handler, { capture: true, once: true });
    }, 0);
  }

  private disarmMarqueeResumeOnNextTap() {
    if (!this.marqueeResumeListener) return;
    this.doc.removeEventListener('click', this.marqueeResumeListener, {
      capture: true,
    } as AddEventListenerOptions);
    this.marqueeResumeListener = null;
  }

  readonly contextualSidebarHeadline = this.content.contextualSidebarHeadline;
  readonly workflowHeadline = this.content.workflowHeadline;
  readonly workflowCards: LandingWorkflowCard[] = this.content.workflowCards;

  readonly comparisonHeadline = this.content.comparisonHeadline;

  readonly comparisonGeneric: LandingComparisonPoint[] = this.content.comparisonGeneric;

  readonly comparisonPcChat: LandingComparisonPoint[] = this.content.comparisonPcChat;

  readonly comparisonRows: ComparisonRow[] = buildComparisonRows(
    this.comparisonGeneric,
    this.comparisonPcChat,
  );

  readonly metrics = signal<LandingMetric[]>(this.content.metrics.map((m) => ({ ...m })));

  readonly useCases = this.content.useCases;

  readonly useCaseTeams: LandingUseCaseTeam[] = this.content.useCaseTeams;

  readonly activeUseCaseTeamId = signal<LandingUseCaseTeam['id'] | null>('investment');

  readonly activeTeam = computed(
    () => this.useCaseTeams.find((t) => t.id === this.activeUseCaseTeamId()) ?? null,
  );

  toggleTeam(teamId: LandingUseCaseTeam['id']) {
    if (!this.isBelow1250()) {
      // desktop behavior: always switch
      this.activeUseCaseTeamId.set(teamId);
      return;
    }

    // mobile / tablet accordion behavior
    this.activeUseCaseTeamId.update((current) => (current === teamId ? null : teamId));
  }

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly plans = signal<PaymentPlan[]>([]);

  readonly finalCta = this.content.finalCta;

  readonly footerLeft = this.content.footerLeft;

  readonly footerRightColumns: FooterColumn[] = this.content.footerRightColumns;

  readonly footerSocial = this.content.footerSocial;

  // Accordion open/close state (≤1024px footer variant).
  readonly openFooterSectionId = signal<FooterColumn['id'] | null>(null);

  toggleFooterSection(sectionId: FooterColumn['id']) {
    this.openFooterSectionId.update((cur) => (cur === sectionId ? null : sectionId));
  }

  private observer!: IntersectionObserver;
  @ViewChildren('elevateOnScroll', { read: ElementRef })
  private elevateOnScrollEls!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('heroVideo') heroVideo?: ElementRef<HTMLVideoElement>;

  ngAfterViewInit() {
    if (!this.isBrowser) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const ratio = entry.intersectionRatio;

          if (ratio > 0.95) {
            entry.target.classList.add('is-visible');
          } else if (ratio < 0.85) {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      {
        threshold: [0, 0.85, 0.95, 1],
      },
    );

    this.elevateOnScrollEls.forEach((ref) => this.observer.observe(ref.nativeElement));
    this.elevateOnScrollEls.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.elevateOnScrollEls.forEach((ref) => this.observer.observe(ref.nativeElement)),
      );
  }

  ngOnDestroy() {
    this.theme.forceTheme(null);
    this.observer?.disconnect();
    this.disarmMarqueeResumeOnNextTap();
    const w = this.doc.defaultView;
    if (w && this.startupLoaderTimeoutId !== null) {
      w.clearTimeout(this.startupLoaderTimeoutId);
      this.startupLoaderTimeoutId = null;
    }
  }

  constructor() {
    this.theme.forceTheme('light');

    if (!this.isBrowser) {
      this.startupLoading.set(false);
      return;
    }

    this.startupLoading.set(true);

    effect(() => {
      if (this.didKickHeroVideo) return;
      if (this.showStartupLoader()) return;

      const w = this.doc.defaultView;
      if (!w) return;

      this.didKickHeroVideo = true;
      w.setTimeout(() => {
        const v = this.heroVideo?.nativeElement;
        if (!v) {
          this.didKickHeroVideo = false;
          return;
        }

        v.muted = true;
        v.play().catch(() => {});
      }, 0);
    });

    const hasRedirectParam = this.captureRedirectParam();
    if (!hasRedirectParam) {
      this.tryRedirectFromSession();
    }
    this.tryExchangeShortLivedToken();
    this.tryRedirectIfSessionAuthenticated();
    afterNextRender(() => {
      this.loadSubscriptionPlans();
      this.loadCoverageMetrics();
    });
  }

  private loadCoverageMetrics(): void {
    const url = 'https://privatecircle.co/homepage/our_coverage/';

    this.http
      .get<CoverageMetricApiRow[]>(url)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((rows) => (Array.isArray(rows) ? rows : [])),
        catchError(() => of<CoverageMetricApiRow[]>([])),
      )
      .subscribe((rows) => {
        const countsByLabel = new Map<string, number>();
        for (const row of rows) {
          const label = typeof row?.label === 'string' ? row.label.trim() : '';
          const count = typeof row?.count === 'number' ? row.count : Number(row?.count);
          if (!label || !Number.isFinite(count)) continue;
          countsByLabel.set(label, count);
        }

        const next = this.metrics().map((m) => {
          const desiredApiLabel = this.coverageApiLabelForMetricLabel(m.label);
          if (!desiredApiLabel) return m;

          const count = countsByLabel.get(desiredApiLabel);
          if (typeof count !== 'number') return m;

          return { ...m, count, value: this.formatCoverageCount(count) };
        });

        this.metrics.set(next);
      });
  }

  private coverageApiLabelForMetricLabel(metricLabel: string): string {
    // We only display 4 landing metrics; the API returns 6 rows.
    switch (metricLabel) {
      case 'COMPANIES TRACKED':
        return '# Companies';
      case 'DEALS COVERED':
        return '# Deals covered';
      case 'VC/PE FUNDS PROFILED':
        return '# VC/PE funds profiled';
      case 'DEBT CHARGES':
        return '# Charges covered';
      default:
        return '';
    }
  }

  private formatCoverageCount(count: number): string {
    const n = Number(count);
    if (!Number.isFinite(n)) return '';

    const abs = Math.abs(n);

    if (abs >= 1_000_000) {
      const val = Math.floor(abs / 100_000) / 10; // truncate to 1 decimal (millions)
      return `${val.toFixed(1).replace(/\.0$/, '')} Mn+`;
    }

    if (abs >= 1_000) {
      if (abs >= 100_000) {
        const val = Math.floor(abs / 1_000); // truncate (thousands)
        return `${val} K+`;
      }

      const val = Math.floor(abs / 100) / 10; // truncate to 1 decimal (thousands)
      return `${val.toFixed(1).replace(/\.0$/, '')} K+`;
    }

    return abs.toString();
  }

  login() {
    const referer = this.buildReferer();
    const utmQuery = this.buildUtmQuery();
    const url = `${environment.authUiBaseUrl}/login?referer=${referer}${utmQuery}`;
    this.fullPageNavigate(url);
  }

  register() {
    const referer = this.buildReferer();
    const utmQuery = this.buildUtmQuery();
    const url = `${environment.authUiBaseUrl}/register?referer=${referer}${utmQuery}`;
    this.fullPageNavigate(url);
  }

  /**
   * Builds the `referer` query-param value for the auth UI.
   *
   * If a redirect path is saved (e.g. `/plans`), the referer becomes
   * `encodeURIComponent(origin)/plans` so the auth service redirects back
   * to `origin/plans/get_access?short_lived_token=…`.
   *
   * Otherwise falls back to just the encoded origin (`origin/get_access?…`).
   */
  private buildReferer(): string {
    const origin = this.origin();
    const redirectPath = this.getSavedRedirectPath();
    return redirectPath
      ? `${encodeURIComponent(origin)}${redirectPath}`
      : encodeURIComponent(origin);
  }

  /**
   * Returns a query-string fragment with captured UTM params (e.g. `&utm_source=linkedin&utm_medium=email`).
   * Returns an empty string if no UTM params were captured.
   */
  private buildUtmQuery(): string {
    const params = this.utmTracking.getParams();
    const entries = Object.entries(params).filter(([, v]) => !!v);
    if (!entries.length) return '';
    return '&' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join('&');
  }

  /**
   * Extracts the pathname portion from the redirect URL stored in sessionStorage.
   * Returns an empty string if nothing is saved or the path is just `/`.
   */
  private getSavedRedirectPath(): string {
    const saved = this.getRedirectUrl();
    if (!saved) return '';
    try {
      const u = new URL(saved);
      return u.pathname !== '/' ? u.pathname : '';
    } catch {
      return '';
    }
  }

  private origin(): string {
    return this.doc.defaultView?.location?.origin ?? '';
  }

  private fullPageNavigate(url: string) {
    const w = this.doc.defaultView;
    if (!w) return;
    w.location.assign(url);
  }

  private tryExchangeShortLivedToken() {
    const w = this.doc.defaultView;
    if (!w) return;

    const token = this.getShortLivedToken();

    if (!token) return;

    this._state.set('exchanging');

    const redirectUrl = this.resolvePostLoginUrl();
    this.setRedirectUrl(redirectUrl);
    const httpParams = new HttpParams()
      .set('short_lived_token', token)
      .set('redirect_url', redirectUrl);

    // API call first (so we control post-success navigation), then route to the intended destination.
    // Backend may respond with a redirect; XHR will follow it, but cookies should still be set.
    void firstValueFrom(this.http.get('/get_access', { params: httpParams, withCredentials: true }))
      .then(async () => {
        // Clear the token from URL/storage to prevent re-exchange loops on refresh.
        this.clearShortLivedToken();

        // UTM params have been sent to the auth service; clean up sessionStorage.
        this.utmTracking.clear();

        // Mark authenticated based on successful exchange; don't immediately call /token/refresh.
        // Calling refresh right away can accidentally reuse a stale refresh cookie and overwrite fresh tokens.
        this.auth.markAuthenticated();

        let ok = false;
        try {
          const target = new URL(redirectUrl);
          ok = await this.router.navigateByUrl(`${target.pathname}${target.search}${target.hash}`);
        } catch {
          ok = await this.router.navigate(['/app']);
        }
        if (ok) {
          this.clearRedirectUrl();
          return;
        }
        this._state.set('idle');
      })
      .catch(() => {
        // Prevent endless retries with a stale/consumed token.
        this.clearShortLivedToken();
        this._state.set('idle');
        // If the user already has a valid cookie session, still allow them into the app.
        this.tryRedirectIfSessionAuthenticated();
      });
  }

  private tryRedirectIfSessionAuthenticated() {
    const w = this.doc.defaultView;
    if (!w) return;

    const hasToken = !!new URLSearchParams(w.location.search ?? '').get('short_lived_token');
    if (hasToken) return;

    this.startupLoading.set(true);

    void firstValueFrom(this.auth.probeSession())
      .then((ok) => {
        if (ok) {
          const postLoginUrl = this.resolvePostLoginUrl();
          this.clearRedirectUrl();
          try {
            const u = new URL(postLoginUrl);
            void this.router.navigateByUrl(`${u.pathname}${u.search}${u.hash}`);
          } catch {
            void this.router.navigate(['/app']);
          }
          return;
        }

        // Not authenticated → keep loader visible for 1s, then show landing.
        if (this.startupLoaderTimeoutId !== null) w.clearTimeout(this.startupLoaderTimeoutId);
        this.startupLoaderTimeoutId = w.setTimeout(() => {
          this.startupLoaderTimeoutId = null;
          this.startupLoading.set(false);
        }, 1000);
      })
      .catch(() => {
        // Treat errors as unauthenticated.
        if (this.startupLoaderTimeoutId !== null) w.clearTimeout(this.startupLoaderTimeoutId);
        this.startupLoaderTimeoutId = w.setTimeout(() => {
          this.startupLoaderTimeoutId = null;
          this.startupLoading.set(false);
        }, 1000);
      });
  }

  private getShortLivedToken(): string {
    const w = this.doc.defaultView;
    if (!w) return '';

    const fromSearch = new URLSearchParams(w.location.search ?? '').get('short_lived_token') ?? '';
    if (fromSearch.trim()) {
      try {
        w.sessionStorage?.setItem(
          'pc.auth.short_lived_token',
          JSON.stringify({ token: fromSearch.trim(), savedAt: Date.now() }),
        );
      } catch {
        // ignore storage failures
      }
      return fromSearch.trim();
    }

    // Some auth systems append tokens in hash fragments.
    const hash = (w.location.hash ?? '').replace(/^#/, '');
    const fromHash =
      new URLSearchParams(hash.startsWith('?') ? hash.slice(1) : hash).get('short_lived_token') ??
      '';
    if (fromHash.trim()) return fromHash.trim();

    try {
      const raw = (w.sessionStorage?.getItem('pc.auth.short_lived_token') ?? '').trim();
      if (!raw) return '';

      // Backwards compatible: older versions stored the token as a raw string.
      if (!raw.startsWith('{')) return raw;

      const parsed = JSON.parse(raw) as { token?: unknown; savedAt?: unknown };
      const token = typeof parsed?.token === 'string' ? parsed.token.trim() : '';
      const savedAt = typeof parsed?.savedAt === 'number' ? parsed.savedAt : 0;
      const isFresh = savedAt > 0 && Date.now() - savedAt < 2 * 60_000;

      if (!token || !isFresh) {
        w.sessionStorage?.removeItem('pc.auth.short_lived_token');
        return '';
      }

      return token;
    } catch {
      return '';
    }
  }

  private clearShortLivedToken() {
    const w = this.doc.defaultView;
    if (!w) return;

    try {
      w.sessionStorage?.removeItem('pc.auth.short_lived_token');
    } catch {
      // ignore
    }

    // Remove token from URL without a navigation.
    try {
      const url = new URL(w.location.href);
      url.searchParams.delete('short_lived_token');
      w.history.replaceState({}, '', url.toString());
    } catch {
      // ignore
    }
  }

  private tryRedirectFromSession() {
    const w = this.doc.defaultView;
    if (!w) return;

    const url = this.getRedirectUrl();
    if (!url) return;

    // If token is present, exchange flow will handle navigation.
    const hasToken = !!new URLSearchParams(w.location.search ?? '').get('short_lived_token');
    if (hasToken) return;

    this.clearRedirectUrl();

    try {
      const u = new URL(url);
      if (u.origin === this.origin()) {
        void this.router.navigateByUrl(`${u.pathname}${u.search}${u.hash}`);
        return;
      }
    } catch {
      // ignore and fallback
    }

    w.location.assign(url);
  }

  private setRedirectUrl(url: string) {
    const w = this.doc.defaultView;
    if (!w) return;
    try {
      w.sessionStorage?.setItem('pc.auth.redirect_url', url);
    } catch {
      // ignore
    }
  }

  private getRedirectUrl(): string {
    const w = this.doc.defaultView;
    if (!w) return '';
    try {
      return (w.sessionStorage?.getItem('pc.auth.redirect_url') ?? '').trim();
    } catch {
      return '';
    }
  }

  private clearRedirectUrl() {
    const w = this.doc.defaultView;
    if (!w) return;
    try {
      w.sessionStorage?.removeItem('pc.auth.redirect_url');
    } catch {
      // ignore
    }
  }

  /**
   * Captures the intended post-login redirect path and saves it to sessionStorage.
   *
   * Two sources are checked (in order):
   * 1. A `?redirect=/path` query param on the current URL (e.g. `/?redirect=/plans`).
   * 2. The **original navigation URL** via the Performance API. When the auth service
   *    redirects to `http://origin/plans/get_access?short_lived_token=…`, Angular's
   *    router rewrites the URL before this component loads. The Performance API still
   *    holds the original URL, so we can extract `/plans` from `/plans/get_access`.
   *
   * Only paths starting with `/` are accepted (prevents open-redirect attacks).
   * @returns `true` if a redirect path was captured.
   */
  private captureRedirectParam(): boolean {
    const w = this.doc.defaultView;
    if (!w) return false;

    // 1. Explicit ?redirect= query param (e.g. /?redirect=/plans)
    const redirect = new URLSearchParams(w.location.search ?? '').get('redirect') ?? '';
    if (redirect && redirect.startsWith('/')) {
      this.setRedirectUrl(`${this.origin()}${redirect}`);
      return true;
    }

    // 2. Extract from the original navigation URL when the auth service embeds
    //    the path in the URL (e.g. /plans/get_access?short_lived_token=…).
    //    Angular routing rewrites the URL before this component loads, but the
    //    Performance Navigation Timing API preserves the original entry URL.
    if (!this.getRedirectUrl()) {
      try {
        const navEntries = w.performance?.getEntriesByType?.('navigation') as
          | PerformanceNavigationTiming[]
          | undefined;
        const initialUrl = navEntries?.[0]?.name;
        if (initialUrl) {
          const u = new URL(initialUrl);
          const hasToken = u.searchParams.has('short_lived_token');
          if (hasToken && u.pathname.includes('/get_access')) {
            const redirectPath = u.pathname.split('/get_access')[0];
            if (redirectPath && redirectPath !== '/' && redirectPath.startsWith('/')) {
              this.setRedirectUrl(`${this.origin()}${redirectPath}`);
              return true;
            }
          }
        }
      } catch {
        // Performance API unavailable or parsing failed; fall through.
      }
    }

    return false;
  }

  /**
   * Returns the post-login destination URL.
   * Reads from sessionStorage (set by `captureRedirectParam`), falls back to `${origin}/app`.
   */
  private resolvePostLoginUrl(): string {
    return this.getRedirectUrl() || `${this.origin()}/app`;
  }

  loadSubscriptionPlans(): void {
    this.loading.set(true);
    this.error.set(null);

    this.paymentsApi
      .listPlans()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err: unknown) => {
          this.error.set(err instanceof Error ? err.message : 'Failed to load plans.');
          this.plans.set([]);
          return of<PaymentPlan[]>([]);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((plans) => {
        this.plans.set(Array.isArray(plans) ? plans : []);
      });
  }

  onSelected(plan: PaymentPlan): void {
    this.register();
  }
}
